import React, { useEffect, useRef, useState } from "react";
import currencyCodes from "currency-codes";
import {
  Box,
  Collapse,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Icon,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
  Button,
} from "@chakra-ui/react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { FaCheckCircle } from "react-icons/fa";
import { ColumnInfo, FilterProto, Header, ObservationColumnProto } from "build-ts/metadata";
import {
  AggregationType,
  FilterOperator,
  MetricType,
  PolarityEnum,
  polarityEnumToJSON,
} from "build-ts/enums";
import ObservationComponent from "../../containers/configuration/ObservationContainer";
import { DateFilter } from "build-ts/relative_date";
import { DateFilterCategory } from "utils/enum";
import { useEditorShellTokens } from "utils/editorShellStyles";
import { TooltipHintIcon } from "components/common/TooltipHintIcon";

interface EditMetricDefinitionProps {
  metricName: string;
  modelName: string;
  metricDescription: string;
  selectedObservationColumn: Header;
  columns: ColumnInfo[];
  selectedAggregation: AggregationType | undefined;
  selectedObservationColumns: ObservationColumnProto[];
  selectedFilterColumn: ColumnInfo;
  selectedOperator: FilterOperator | undefined;
  inputValue: string;
  selectedFilterColumns: FilterProto[];
  decimalPlaces: number | null;
  polarity: PolarityEnum | undefined;
  metricType: MetricType | undefined;
  currency: string | undefined;
  suffix: string | undefined;
  hasContent: boolean;
  displayType: string;
  columnNames: string[];
  selectedColumnValues: string[];
  dateFilter: DateFilter | undefined;
  dateFilterCategory: DateFilterCategory | undefined;
  createMetricFlag: boolean;
  formula: string;

  onFormulaChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDescriptionChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleDeleteObservationColumn: (indexToDelete: number) => void;
  handleDeleteFilterColumn: (indexToDelete: number) => void;
  submit: () => void;
  handlePolarityChange: (value: string) => void;
  handleMetricTypeChange: (isRatio: boolean) => void;
  handleCurrencyChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handlePercentageChange: (value: boolean) => void;
  handleSuffixChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleToggle: (value: string) => void;

  setDisplayType: React.Dispatch<React.SetStateAction<string>>;
  setDecimalPlaces: React.Dispatch<React.SetStateAction<number | null>>;
  setColumnNames: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedColumnValues: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedObservationColumn: React.Dispatch<React.SetStateAction<Header | null>>;
  setSelectedFilterColumn: React.Dispatch<React.SetStateAction<ColumnInfo | null>>;
  setSelectedAggregation: React.Dispatch<React.SetStateAction<AggregationType | undefined>>;
  setSelectedOperator: React.Dispatch<React.SetStateAction<FilterOperator | undefined>>;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  setSelectedFilterColumns: React.Dispatch<React.SetStateAction<FilterProto[]>>;
  setSelectedObservationColumns: React.Dispatch<React.SetStateAction<ObservationColumnProto[]>>;
  setDateFilter: React.Dispatch<React.SetStateAction<DateFilter | undefined>>;
  setDateFilterCategory: React.Dispatch<React.SetStateAction<DateFilterCategory>>;

  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  compact?: boolean;
}

const EditMetricDefinition: React.FC<EditMetricDefinitionProps> = ({
  metricName,
  modelName,
  metricDescription,
  selectedObservationColumn,
  columns,
  selectedAggregation,
  selectedFilterColumn,
  selectedObservationColumns,
  selectedFilterColumns,
  selectedOperator,
  inputValue,
  displayType,
  columnNames,
  selectedColumnValues,
  decimalPlaces,
  polarity,
  metricType,
  currency,
  suffix,
  dateFilter,
  dateFilterCategory,
  createMetricFlag,
  formula,

  onFormulaChange,
  handleNameChange,
  handleDescriptionChange,
  submit,
  handlePolarityChange,
  handleMetricTypeChange,
  handleCurrencyChange,
  handlePercentageChange,
  handleSuffixChange,

  setDisplayType,
  setDecimalPlaces,
  setSelectedObservationColumn,
  setColumnNames,
  setSelectedFilterColumn,
  setSelectedColumnValues,
  setSelectedAggregation,
  setSelectedOperator,
  setInputValue,
  setSelectedFilterColumns,
  setSelectedObservationColumns,
  setDateFilter,
  setDateFilterCategory,
  isLoading,
  setIsLoading,
  compact,
}) => {
  const compactShell = compact ?? false;
  const [showObservation, setShowObservation] = useState(!compactShell);
  const [showFilters, setShowFilters] = useState(!compactShell);
  /** Plain vs custom suffix: keeps suffix field visible after choosing Custom until Plain is chosen */
  const [customUnitIntent, setCustomUnitIntent] = useState(false);
  const suffixInputRef = useRef<HTMLInputElement>(null);
  const t = useEditorShellTokens(compactShell);

  useEffect(() => {
    if (compactShell) {
      setShowFilters(false);
    }
  }, [compactShell]);

  useEffect(() => {
    if (displayType !== "Regular") {
      setCustomUnitIntent(false);
      return;
    }
    if (suffix?.trim()) {
      setCustomUnitIntent(true);
    }
  }, [displayType, suffix]);

  const polarityKey =
    polarity !== undefined && polarity !== null
      ? polarityEnumToJSON(polarity)
      : "POSITIVE";
  const isMoreIsBetter = polarityKey === "POSITIVE";

  const emptySuffix = !suffix?.trim();
  const plainActive =
    displayType === "Regular" && emptySuffix && !customUnitIntent;
  const customActive =
    displayType === "Regular" && (customUnitIntent || !emptySuffix);
  const percentActive = displayType === "Percentage";
  const currencyActive = displayType === "Currency";

  const fireSuffix = (value: string) => {
    handleSuffixChange({
      target: { value },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const selectPlain = () => {
    setCustomUnitIntent(false);
    setDisplayType("Regular");
    handlePercentageChange(false);
    fireSuffix("");
  };

  const selectCustom = () => {
    setDisplayType("Regular");
    handlePercentageChange(false);
    setCustomUnitIntent(true);
    requestAnimationFrame(() => suffixInputRef.current?.focus());
  };

  const selectPercent = () => {
    setDisplayType("Percentage");
    handlePercentageChange(true);
  };

  const selectCurrency = () => {
    setDisplayType("Currency");
    handlePercentageChange(false);
  };

  const SectionBar = ({ label }: { label: string }) => (
    <Box
      bg="gray.50"
      px={t.sectionPx}
      py={t.sectionPy}
      borderTopWidth="1px"
      borderBottomWidth="1px"
      borderColor="gray.700"
    >
      <Text
        fontWeight="600"
        fontSize={t.sectionTitleSize}
        color="gray.600"
        textAlign="left"
        letterSpacing="normal"
        mb={0}
      >
        {label}
      </Text>
    </Box>
  );

  const CollapsibleBar = ({
    label,
    open,
    onToggle,
  }: {
    label: string;
    open: boolean;
    onToggle: () => void;
  }) => (
    <HStack
      bg="gray.50"
      px={t.sectionPx}
      py={t.sectionPy}
      justify="space-between"
      cursor="pointer"
      userSelect="none"
      onClick={onToggle}
      _hover={{ bg: "gray.100" }}
      borderTopWidth="1px"
      borderBottomWidth="1px"
      borderColor="gray.700"
    >
      <Text
        fontWeight="600"
        fontSize={t.sectionTitleSize}
        color="gray.800"
        textAlign="left"
        letterSpacing="normal"
        mb={0}
      >
        {label}
      </Text>
      <Icon as={open ? FiChevronUp : FiChevronDown} boxSize={t.iconBox} color="gray.600" />
    </HStack>
  );

  return (
    <VStack spacing={0} align="stretch">

      {/* ── Core ── */}
      <SectionBar label="Properties" />
      <VStack
        spacing={t.vStackSpacing}
        align="stretch"
        px={t.contentPx}
        pt={t.contentPt}
        pb={t.contentPb}
        bg="white"
      >
        <FormControl>
          <FormLabel {...t.label}>Name</FormLabel>
          <Input
            {...t.field}
            size={t.inputSize}
            placeholder="Metric name"
            value={metricName}
            onChange={handleNameChange}
          />
        </FormControl>

        <FormControl>
          <FormLabel {...t.label}>Semantics</FormLabel>
          <Textarea
            {...t.field}
            size={t.inputSize}
            rows={t.textareaRows}
            placeholder="Describe what this metric measures, when to use it, and any synonyms or aliases…"
            value={metricDescription}
            onChange={handleDescriptionChange}
            resize="none"
          />
        </FormControl>

        <HStack spacing={t.vStackSpacing} align="flex-start" w="100%">
          <Box flex="1" textAlign="left" minW={0}>
            <Text {...t.label} mb={1}>Units</Text>
            {/* Step 1 — full width, single row (grid) so compact panels don’t wrap oddly */}
            <Grid
              templateColumns="repeat(4, minmax(0, 1fr))"
              gap={0.5}
              p={0.5}
              bg="gray.100"
              borderRadius="md"
              w="100%"
              mb={2}
            >
              <Button
                size={t.compact ? "2xs" : "sm"}
                variant={plainActive ? "solid" : "ghost"}
                colorScheme="purple"
                fontWeight="600"
                borderRadius="md"
                px={1}
                onClick={selectPlain}
              >
                None
              </Button>
              <Button
                size={t.compact ? "2xs" : "sm"}
                variant={percentActive ? "solid" : "ghost"}
                colorScheme="purple"
                fontWeight="600"
                borderRadius="md"
                px={1}
                onClick={selectPercent}
              >
                %
              </Button>
              <Button
                size={t.compact ? "2xs" : "sm"}
                variant={currencyActive ? "solid" : "ghost"}
                colorScheme="purple"
                fontWeight="600"
                borderRadius="md"
                px={1}
                onClick={selectCurrency}
              >
                {t.compact ? "CCY" : "CCY"}
              </Button>
              <Button
                size={t.compact ? "2xs" : "sm"}
                variant={customActive ? "solid" : "ghost"}
                colorScheme="purple"
                fontWeight="600"
                borderRadius="md"
                px={1}
                onClick={selectCustom}
              >
                Custom
              </Button>
            </Grid>

            {/* Step 2 — full width, only when relevant */}
            {displayType === "Currency" && (
              <Select
                {...t.field}
                size={t.inputSize}
                w="100%"
                placeholder="Select currency"
                value={currency || ""}
                onChange={handleCurrencyChange}
              >
                {currencyCodes.data.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.currency}
                  </option>
                ))}
              </Select>
            )}

            {displayType === "Regular" && customUnitIntent && (
              <Input
                {...t.field}
                ref={suffixInputRef}
                size={t.inputSize}
                w="100%"
                placeholder="Unit / suffix (e.g. K, units, hrs)"
                value={suffix ?? ""}
                onChange={handleSuffixChange}
              />
            )}
          </Box>

          <FormControl flex="1" minW={0}>
            <HStack mb={1} spacing={1} w="fit-content">
              <Text {...t.label} mb={0}>
                Precision
              </Text>
              <TooltipHintIcon
                tooltipKey="edit_metric_definition.precision.tooltip"
                size={t.compact ? "sm" : "md"}
              />
            </HStack>
            <Input
              {...t.field}
              size={t.inputSize}
              type="number"
              placeholder="0"
              value={decimalPlaces ?? ""}
              onChange={(e) => setDecimalPlaces(e.target.value ? Number(e.target.value) : null)}
            />
          </FormControl>
        </HStack>

        <HStack spacing={t.vStackSpacing} align="flex-start" w="100%">
          <Box flex="1" textAlign="left" minW={0}>
            <HStack mb={2} align="center" spacing={1}>
              <Text {...t.label} mb={0}>
                Metric type
              </Text>
              <TooltipHintIcon
                tooltipKey="edit_metric_definition.metric_type.tooltip"
                size={t.compact ? "sm" : "md"}
              />
            </HStack>
            <HStack spacing={0} p={0.5} bg="gray.100" borderRadius="md" w="fit-content">
              <Button
                size={t.compact ? "xs" : "sm"}
                variant={metricType !== MetricType.RATIO ? "solid" : "ghost"}
                colorScheme="purple"
                fontWeight="600"
                borderRadius="md"
                onClick={() => handleMetricTypeChange(false)}
              >
                Additive
              </Button>
              <Button
                size={t.compact ? "xs" : "sm"}
                variant={metricType === MetricType.RATIO ? "solid" : "ghost"}
                colorScheme="purple"
                fontWeight="600"
                borderRadius="md"
                onClick={() => handleMetricTypeChange(true)}
              >
                Complex
              </Button>
            </HStack>
          </Box>
          <Box flex="1" textAlign="left" minW={0}>
            <HStack mb={2} align="center" spacing={1}>
              <Text {...t.label} mb={0}>
                Polarity
              </Text>
              <TooltipHintIcon
                tooltipKey="edit_metric_definition.polarity.tooltip"
                size={t.compact ? "sm" : "md"}
              />
            </HStack>
            <HStack spacing={0} p={0.5} bg="gray.100" borderRadius="md" w="fit-content">
              <Button
                size={t.compact ? "xs" : "sm"}
                variant={isMoreIsBetter ? "solid" : "ghost"}
                colorScheme="green"
                fontWeight="600"
                borderRadius="md"
                onClick={() => handlePolarityChange("POSITIVE")}
              >
                Positive
              </Button>
              <Button
                size={t.compact ? "xs" : "sm"}
                variant={!isMoreIsBetter ? "solid" : "ghost"}
                colorScheme="red"
                fontWeight="600"
                borderRadius="md"
                onClick={() => handlePolarityChange("NEGATIVE")}
              >
                Negative
              </Button>
            </HStack>
          </Box>
          <Box flex="1" textAlign="left" minW={0}>
            <FormControl flex="1" minW={0}>
              <HStack mb={1} spacing={1} w="fit-content">
                <Text {...t.label} mb={0}>
                  Formula
                </Text>
                <TooltipHintIcon
                  tooltipKey="edit_metric_definition.formula.tooltip"
                  size={t.compact ? "sm" : "md"}
                />
              </HStack>
              <Input
                {...t.field}
                size={t.inputSize}
                type="text"
                placeholder="Enter formula"
                value={formula ?? ""}
                onChange={onFormulaChange}
              />
            </FormControl>
          </Box>
        </HStack>
      </VStack>
      {/* ── Observation (collapsible) ── */}
      <CollapsibleBar label="Observation" open={showObservation} onToggle={() => setShowObservation(!showObservation)} />
      <Collapse in={showObservation} animateOpacity>
        <Box px={t.contentPx} py={t.filterCollapsePy} bg="white">
          <ObservationComponent
            type="observation"
            columns={columns}
            modelName={modelName}
            columnNames={columnNames}
            selectedObservationColumn={selectedObservationColumn}
            selectedFilterColumn={selectedFilterColumn}
            selectedAggregation={selectedAggregation}
            selectedOperator={selectedOperator}
            inputValue={inputValue}
            selectedColumnValues={selectedColumnValues}
            selectedFilterColumns={selectedFilterColumns}
            selectedObservationColumns={selectedObservationColumns}
            dateFilter={dateFilter}
            dateFilterCategory={dateFilterCategory}
            isLoading={isLoading}
            setSelectedObservationColumn={setSelectedObservationColumn}
            setColumnNames={setColumnNames}
            setSelectedFilterColumn={setSelectedFilterColumn}
            setSelectedColumnValues={setSelectedColumnValues}
            setSelectedAggregation={setSelectedAggregation}
            setSelectedOperator={setSelectedOperator}
            setInputValue={setInputValue}
            setSelectedFilterColumns={setSelectedFilterColumns}
            setSelectedObservationColumns={setSelectedObservationColumns}
            setDateFilter={setDateFilter}
            setDateFilterCategory={setDateFilterCategory}
            setIsLoading={setIsLoading}
            compact={compactShell}
          />
        </Box>
      </Collapse>

      {/* ── Filters (collapsible — secondary) ── */}
      <Box mt={t.vStackSpacing} />
      <CollapsibleBar label="Filters" open={showFilters} onToggle={() => setShowFilters(!showFilters)} />
      <Collapse in={showFilters} animateOpacity>
        <Box px={t.contentPx} py={t.filterCollapsePy} bg="white">
          <ObservationComponent
            type="filter"
            columns={columns}
            modelName={modelName}
            columnNames={columnNames}
            selectedObservationColumn={selectedObservationColumn}
            selectedFilterColumn={selectedFilterColumn}
            selectedAggregation={selectedAggregation}
            selectedOperator={selectedOperator}
            inputValue={inputValue}
            selectedColumnValues={selectedColumnValues}
            selectedFilterColumns={selectedFilterColumns}
            selectedObservationColumns={selectedObservationColumns}
            dateFilter={dateFilter}
            dateFilterCategory={dateFilterCategory}
            isLoading={isLoading}
            setSelectedObservationColumn={setSelectedObservationColumn}
            setColumnNames={setColumnNames}
            setSelectedFilterColumn={setSelectedFilterColumn}
            setSelectedColumnValues={setSelectedColumnValues}
            setSelectedAggregation={setSelectedAggregation}
            setSelectedOperator={setSelectedOperator}
            setInputValue={setInputValue}
            setSelectedFilterColumns={setSelectedFilterColumns}
            setSelectedObservationColumns={setSelectedObservationColumns}
            setDateFilter={setDateFilter}
            setDateFilterCategory={setDateFilterCategory}
            setIsLoading={setIsLoading}
            compact={compactShell}
          />
        </Box>
      </Collapse>

      {/* Submit (create mode only) */}
      {createMetricFlag && (
        <Box px={t.contentPx} pb={t.contentPb} pt={t.compact ? 2 : 3}>
          <Button
            colorScheme="green"
            onClick={submit}
            w="100%"
            fontWeight="bold"
            size={t.compact ? "sm" : "md"}
            leftIcon={<FaCheckCircle />}
          >
            Save
          </Button>
        </Box>
      )}
    </VStack>
  );
};

export default EditMetricDefinition;